import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import Dialog from '@hobs/components/Dialog';
import Toast from '@hobs/components/Toast';
import RadioButton from '@hobs/components/RadioButton';
import ShortId from 'shortid';
import { Formik, Form, Field } from 'formik';
import Input from '@hobs/components/Input';
import { RaisedButton } from '@hobs/components/Buttons';
import InputLabel from '@hobs/components/InputLabel';
import Dropdown from '@hobs/components/Dropdown';
import Alert from '@hobs/components/Alert';
import { UNIT_PARENT_TYPES, UNIT_TYPES } from '@hobs/utils/constants';

import {
  getUnitsForSubmit,
  createUnit,
  getUnit,
  updateUnit,
  deleteUnit,
  clearUnit
} from '../../../actions/units';

import './UnitDialog.scss';

const defaultState = {
  error: null
};

class UnitDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = defaultState;

    const { t } = props;
    this.unitCreateSchema = Yup.object().shape({
      parentUnitId: Yup.string().when('unitTypeName', {
        is: UNIT_TYPES.Unit,
        then: Yup.string().nullable(),
        otherwise: Yup.string()
          .nullable()
          .required(t('Place of the unit is not selected'))
      }),
      title: Yup.string()
        .max(256, t('Title cannot be longer than 256 symbols'))
        .required(t('Title is required')),
      unitTypeName: Yup.string(),
    });

    this.unitUpdateSchema = Yup.object().shape({
      title: Yup.string()
        .max(256, t('Title cannot be longer than 256 symbols'))
        .required(t('Title is required')),
      parentUnitId: Yup.string().when('unitTypeName', {
        is: UNIT_TYPES.Unit,
        then: Yup.string().nullable(),
        otherwise: Yup.string()
          .nullable()
          .required(t('Place of the unit is not selected'))
      }),
    });
  }

  isDialogOpen() {
    return this.props.match.params.action === 'unit';
  }

  getPartyPathName() {
    const deleteIndex = this.props.location.pathname.indexOf('/unit');
    return this.props.location.pathname.substring(0, deleteIndex);
  }

  getUnitTypesOptions(setFieldValue, selectedValue) {
    const { forSubmit } = this.props;
    return Array.isArray(forSubmit) && forSubmit.map(unitType => (
      <div key={unitType.name} className="unit-create--radio-button-option">
        <div>
          <RadioButton
            name="unitTypeName"
            value={unitType.name}
            setFieldValue={setFieldValue}
            onChange={() => {
              setFieldValue('parentUnitId', null);
              setFieldValue('unitOperationTypeId', null);
            }}
            checked={unitType.name === selectedValue}
          />
        </div>
        <InputLabel>{unitType.longName}</InputLabel>
      </div>
    ));
  }

  getUnitsSelectDefaultMessage(unitTypeName, unitId) {
    const { t, units } = this.props;

    const parentUnitType = UNIT_PARENT_TYPES[unitTypeName];
    const flatUnits = this.flattenUnitsTree(units, parentUnitType, unitId);

    if (!flatUnits.length && unitTypeName === UNIT_TYPES.SubUnit) {
      return t('There are no Units in the party structure');
    }

    if (!flatUnits.length && unitTypeName === UNIT_TYPES.Component) {
      return t('There are no Sub-units in the party structure')
    }

    return t('Select location/parent element');
  }

  getUnitsOptions(values) {
    const { units } = this.props;
    const parentUnitType = UNIT_PARENT_TYPES[values.unitTypeName];
    const flatUnits = this.flattenUnitsTree(units, parentUnitType, values.unitId);

    if (flatUnits && flatUnits.length > 0) {
      return this.sortUnits(flatUnits).map(unit => {
        return (
          <option key={ShortId.generate()} value={unit.unitId}>
            {unit.title}
          </option>
        );
      });
    }
  }

  flattenUnitsTree(array, parentUnitType, unitId) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      if (item.unitTypeName === parentUnitType && unitId !== item.unitId) {
        result.push(item);
      }

      if(unitId === item.unitId){
        return result;
      }

      if (Array.isArray(item.units)) {
        result = result.concat(
          this.flattenUnitsTree(item.units, parentUnitType, unitId)
        );
      }

      return result;
    }, []);
  }

  createUnit = (values, { setErrors, setSubmitting }) => {
    const { t, onUnitCreate, units } = this.props;

    this.props.createUnit(values).then(response => {
      if (response.success) {
        const updatedUnits = this.flattenUnitsTree(
          response.payload.units,
          values.unitTypeName
        );

        const parentUnitType = UNIT_PARENT_TYPES[values.unitTypeName];
        const parentUnits = this.flattenUnitsTree(units, parentUnitType);

        const parentUnit = parentUnits.find(unit => {
          return unit.unitId === values.parentUnitId;
        });

        const createdUnit = updatedUnits.find(unit => {
          return unit.title === values.title;
        });

        if (createdUnit && onUnitCreate) {
          onUnitCreate({
            ...createdUnit,
            parentUnit
          });
        }

        this.onDialogClose();

        Toast.success(t('Unit created successfully'));
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [this.toLowercaseFirst(error.target)]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setUnitError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  updateUnit = (values, { setErrors, setSubmitting }) => {
    const { t } = this.props;

    this.props.updateUnit(values).then(response => {
      if (response.success) {
        this.onDialogClose();
        Toast.success(t('Unit information updated successfully'));
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [this.toLowercaseFirst(error.target)]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setUnitError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  toLowercaseFirst(value) {
    if (typeof value !== 'string' || value.length === 0) {
      return value;
    }

    return value.charAt(0).toLowerCase() + value.slice(1);
  }

  onDialogClose = () => {
    const { reloadUnits, history } = this.props;

    history.push(this.getPartyPathName());
    if (reloadUnits) {
      reloadUnits();
    }
  };

  onDialogClosed = () => {
    const { clearUnit } = this.props;

    clearUnit();
    this.resetState();
  };

  onDialogOpening = async () => {
    this.props.getUnitsForSubmit();
    const unitId = this.props.match.params.guid;
    if (unitId) {
      this.props.getUnit(unitId);
    }
  };

  setUnitError(message) {
    this.setState({ error: message });
  }

  resetState() {
    this.setState(defaultState);
  }

  isEdit() {
    const { match, unit } = this.props;
    return match.params.guid || unit;
  }

  sortUnits(units) {
    if (!Array.isArray(units)) {
      return [];
    }

    const sortedUnits = units.sort((a, b) =>
      a.title.toLowerCase() > b.title.toLowerCase()
        ? 1
        : a.title.toLowerCase() < b.title.toLowerCase()
          ? -1
          : 0
    );

    return sortedUnits.map(unit => {
      return {
        ...unit,
        units: this.sortUnits(unit.units)
      };
    });
  }

  render() {
    const { t, unit, partyId } = this.props;
    const { error } = this.state;
    return (
      <Dialog
        title={t(this.isEdit() ? 'Edit element' : 'Add new element')}
        isOpen={this.isDialogOpen()}
        onClose={this.onDialogClose}
        onClosed={this.onDialogClosed}
        onOpening={this.onDialogOpening}
        className="unit-create"
      >
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={
            unit ? this.unitUpdateSchema : this.unitCreateSchema
          }
          enableReinitialize={true}
          initialValues={
            unit
              ? unit
              : {
                partyId: partyId,
                parentUnitId: null,
                unitTypeName: UNIT_TYPES.Unit,
                title: '',
                unitOperationTypeId: null
              }
          }
          onSubmit={unit ? this.updateUnit : this.createUnit}
        >
          {({ setFieldValue, values, isSubmitting }) => (
            <Form>
              {error && <Alert type="error">{error}</Alert>}
              <div className="unit-create--dialog">
                <React.Fragment>
                  <InputLabel>{(!this.isEdit() && t('Add new:')) || (this.isEdit() && t('Unit type:'))}</InputLabel>
                  <div className="unit-create--row">
                    <div className="unit-create-unit-type-select">
                      {this.getUnitTypesOptions(
                        setFieldValue,
                        values.unitTypeName
                      )}
                    </div>
                  </div>
                </React.Fragment>
                <div className="unit-create--row--wide">
                  {values.unitTypeName !== UNIT_TYPES.Unit && !values.unitOperationTypeId && (
                    <Field
                      name="parentUnitId"
                      setFieldValue={setFieldValue}
                      component={Dropdown}
                      placeholder={t('Select location/parent element')}
                      label={t('Select location/parent element')}
                    >
                      <option value="">
                        {this.getUnitsSelectDefaultMessage(values.unitTypeName, values.unitId)}
                      </option>
                      {this.getUnitsOptions(values)}
                    </Field>
                  )}
                </div>
                <div className="unit-create--row--wide">
                  <Field
                    name="title"
                    setFieldValue={setFieldValue}
                    component={Input}
                  >
                    {t('Item title')}
                  </Field>
                </div>
                <div className="unit-create--submit-button">
                  <RaisedButton type="submit" disabled={isSubmitting}>
                    {t('Save')}
                  </RaisedButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
}

UnitDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  reloadUnits: PropTypes.func,
  forSubmit: PropTypes.object,
  partyId: PropTypes.string,
  units: PropTypes.array,
  getUnitsForSubmit: PropTypes.func,
  createUnit: PropTypes.func,
  getUnit: PropTypes.func,
  updateUnit: PropTypes.func,
  deleteUnit: PropTypes.func,
  clearUnit: PropTypes.func,
  unit: PropTypes.object,
  onUnitCreate: PropTypes.func
};

function mapStateToProps(state){
  return{
    forSubmit: state.units.unitsForSubmit,
    unit: state.units.unit
  }
}

const mapDispatchToProps = {
  getUnitsForSubmit,
  createUnit,
  getUnit,
  updateUnit,
  deleteUnit,
  clearUnit
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(UnitDialog))
);
